<script lang="ts">
	/**
	 * Post-test results dashboard showing aggregated performance metrics.
	 */

	import type { TestResults } from './types';

	let { results, onRestart }: { results: TestResults; onRestart: () => void } =
		$props();

	/**
	 * Returns a CSS color class based on a ratio.
	 * Lower is better for error rates, higher is better for hits.
	 */
	function performanceColor(
		value: number,
		total: number,
		invert = false,
	): string {
		if (total === 0) return 'text-gray-400';
		const ratio = value / total;
		if (invert) {
			// Higher is better (e.g. hits)
			if (ratio >= 0.8) return 'text-green-400';
			if (ratio >= 0.5) return 'text-yellow-400';
			return 'text-red-400';
		}
		// Lower is better (e.g. errors)
		if (ratio <= 0.1) return 'text-green-400';
		if (ratio <= 0.3) return 'text-yellow-400';
		return 'text-red-400';
	}

	/** Helper: format ms to integer or show N/A. */
	function fmtMs(ms: number | null): string {
		return ms !== null ? `${Math.round(ms)} ms` : '해당 없음';
	}
</script>

<div class="mx-auto max-w-2xl py-8">
	<h1 class="mb-6 text-3xl font-bold">테스트 결과</h1>

	<div class="grid grid-cols-1 gap-6 md:grid-cols-2">
		<!-- Visual Column -->
		<section class="rounded-lg border border-blue-700 bg-blue-900/30 p-5">
			<h2 class="mb-4 text-xl font-semibold text-blue-300">시각</h2>

			<dl class="space-y-3 text-sm">
				<div class="flex justify-between">
					<dt>정적중</dt>
					<dd class="font-mono {performanceColor(results.visualHits, results.totalTrials, true)}">
						{results.visualHits}
					</dd>
				</div>
				<div class="flex justify-between">
					<dt>누락</dt>
					<dd class="font-mono {performanceColor(results.visualOmissions, results.totalTrials)}">
						{results.visualOmissions}
					</dd>
				</div>
				<div class="flex justify-between">
					<dt>오경보</dt>
					<dd class="font-mono {performanceColor(results.visualCommissions, results.totalTrials)}">
						{results.visualCommissions}
					</dd>
				</div>
				<div class="flex justify-between border-t border-blue-700 pt-2">
					<dt>평균 RT</dt>
					<dd class="font-mono">{fmtMs(results.visualMeanRT)}</dd>
				</div>
				<div class="flex justify-between">
					<dt>RT 표준편차</dt>
					<dd class="font-mono">{fmtMs(results.visualRTStdDev)}</dd>
				</div>
			</dl>
		</section>

		<!-- Auditory Column -->
		<section class="rounded-lg border border-green-700 bg-green-900/30 p-5">
			<h2 class="mb-4 text-xl font-semibold text-green-300">청각</h2>

			<dl class="space-y-3 text-sm">
				<div class="flex justify-between">
					<dt>정적중</dt>
					<dd class="font-mono {performanceColor(results.auditoryHits, results.totalTrials, true)}">
						{results.auditoryHits}
					</dd>
				</div>
				<div class="flex justify-between">
					<dt>누락</dt>
					<dd class="font-mono {performanceColor(results.auditoryOmissions, results.totalTrials)}">
						{results.auditoryOmissions}
					</dd>
				</div>
				<div class="flex justify-between">
					<dt>오경보</dt>
					<dd class="font-mono {performanceColor(results.auditoryCommissions, results.totalTrials)}">
						{results.auditoryCommissions}
					</dd>
				</div>
				<div class="flex justify-between border-t border-green-700 pt-2">
					<dt>평균 RT</dt>
					<dd class="font-mono">{fmtMs(results.auditoryMeanRT)}</dd>
				</div>
				<div class="flex justify-between">
					<dt>RT 표준편차</dt>
					<dd class="font-mono">{fmtMs(results.auditoryRTStdDev)}</dd>
				</div>
			</dl>
		</section>
	</div>

	<div class="mt-6 text-center">
		<button
			onclick={onRestart}
			class="cursor-pointer rounded-lg bg-blue-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
		>
			다시 테스트
		</button>
	</div>
</div>
